import { useMemo, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import type { i18n } from 'i18next';
import { createEisTheme } from '@eis/theme';
import {
  ApiClientsProvider,
  AuthProvider,
  ViewLevelProvider,
  createApiClients,
  type AuthAdapter,
  type RuntimeConfig,
} from '@eis/core';
import { moduleRegistry } from '../app/modules';

/**
 * Provider order matters: theme wraps everything so an error state is still themed; auth sits
 * above the router so guards can read the permission set; query sits above the routes that use it.
 */
export function AppProviders({
  config,
  adapter,
  i18nInstance,
  children,
}: {
  readonly config: RuntimeConfig;
  /** Built once at bootstrap by `loadAuthAdapter` (./auth-adapter) — the OIDC one lazily. */
  readonly adapter: AuthAdapter;
  readonly i18nInstance: i18n;
  readonly children: ReactNode;
}): ReactNode {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Insurance data is read far more often than it changes; refetching on every window
            // focus makes grids flicker for no benefit.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    [],
  );

  /**
   * The clients take the ADAPTER's token getter, not a token: it is called per request, so a
   * silent refresh reaches the next call without anything being rebuilt, and the auth layer stays
   * the only place that knows what a token's lifetime is.
   *
   * They also take the adapter's refresh-now, its session-lost handler and its own refresh
   * endpoints (SDD-019 S-6, S-11): a request refused with 401/403 is refreshed and replayed once,
   * through authservice (`oidc`) or the legacy refresh (`legacy-session`) — the SDK does not know
   * which. The mock adapter has none, and its clients retry nothing.
   */
  const clients = useMemo(
    () =>
      createApiClients({
        apiBaseUrl: config.apiBaseUrl,
        tokenProvider: () => adapter.getAccessToken(),
        ...(adapter.sessionRefresher === undefined
          ? {}
          : { sessionRefresher: adapter.sessionRefresher }),
        ...(adapter.onSessionLost === undefined ? {} : { onSessionLost: adapter.onSessionLost }),
        ...(adapter.isSessionEndpoint === undefined
          ? {}
          : { isSessionEndpoint: adapter.isSessionEndpoint }),
      }),
    [config.apiBaseUrl, adapter],
  );

  const catalog = useMemo(() => moduleRegistry.permissionCatalog, []);

  // One theme per chrome variant (SDD-012, U-2). A light-only variant pins the mode: the legacy
  // has no dark scheme, and `system` would darken the page for an operator whose OS is dark while
  // the sidebar and header stayed as the legacy paints them.
  const theme = createEisTheme(config.chrome.variant);

  return (
    <ThemeProvider theme={theme} defaultMode={theme.eis.lightOnly ? 'light' : 'system'}>
      <CssBaseline />
      <I18nextProvider i18n={i18nInstance}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider adapter={adapter} catalog={catalog} autoSignIn={adapter.kind !== 'mock'}>
            <ApiClientsProvider clients={clients}>
              <ViewLevelProvider>{children}</ViewLevelProvider>
            </ApiClientsProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
